import { Fragment, useEffect, useRef, useState } from 'react';
import { Transition } from '@headlessui/react';
import { IJxtTooltip } from './types';
import {
  useFloating,
  useDismiss,
  useInteractions,
  flip,
  offset,
  FloatingPortal,
  UseFloatingOptions,
} from '@floating-ui/react';
import { useDebouncedValue } from '@mantine/hooks';

const SHOW_TOOLTIP_DELAY = 500;
// A tooltip opened by a tap has no "pointer out" to close it: it closes by itself after a while.
const TOUCH_TOOLTIP_DURATION = 3000;
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"]';
const DEFAULT_TOOLTIP_CONFIG: UseFloatingOptions = {
  strategy: 'absolute',
  placement: 'bottom-start',
  middleware: [
    offset(4),
    flip({
      fallbackPlacements: ['bottom-start', 'top', 'top-start', 'left'],
    }),
  ],
};

const JxtTooltip = ({ id, trigger, children, config, delay }: IJxtTooltip) => {
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [debouncedShowTooltip] = useDebouncedValue(showTooltip, delay ?? SHOW_TOOLTIP_DELAY);
  const touchTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Any other opening or closing (a dismiss, a hover on a hybrid device) cancels the touch timer: left
  // running, it would close a tooltip opened since.
  const setOpen = (open: boolean) => {
    clearTimeout(touchTimer.current);
    setShowTooltip(open);
  };
  const { refs, floatingStyles, context } = useFloating({
    ...(config ?? DEFAULT_TOOLTIP_CONFIG),
    open: showTooltip,
    onOpenChange: setOpen,
  });
  // A tap elsewhere or a scroll closes it. Not Escape: it belongs to the modal the tooltip may sit in.
  const dismiss = useDismiss(context, { ancestorScroll: true, escapeKey: false });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  useEffect(() => () => clearTimeout(touchTimer.current), []);

  return (
    <div
      role="tooltip"
      aria-describedby={`tooltip-${id}`}
      onPointerDown={(e) => {
        // A tap on a button or a link does its action (open a modal…): no tooltip on top of it.
        // Only one inside the tooltip: a clickable ancestor (a grid's role="button" cell) still shows it.
        const interactive = (e.target as Element).closest(INTERACTIVE_SELECTOR);
        if (e.pointerType !== 'touch' || (interactive && e.currentTarget.contains(interactive))) {
          return;
        }
        setOpen(!showTooltip);
        if (!showTooltip) {
          touchTimer.current = setTimeout(() => setShowTooltip(false), TOUCH_TOOLTIP_DURATION);
        }
      }}
      onPointerOver={(e) => {
        // avoid trigger popover on touch devices
        if (e.pointerType === 'mouse') {
          setOpen(true);
        }
      }}
      onPointerOut={(e) => {
        if (e.pointerType === 'mouse') {
          setOpen(false);
        }
      }}
    >
      <div ref={refs.setReference} {...getReferenceProps()}>
        {trigger}
      </div>
      <FloatingPortal id={`tooltip-${id}`}>
        <Transition
          // we want the tooltip to be displayed after the delay, but to be hidden immediately after the user mouse out
          show={debouncedShowTooltip && showTooltip}
          as={Fragment}
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
            {children}
          </div>
        </Transition>
      </FloatingPortal>
    </div>
  );
};

export default JxtTooltip;
